<template>
  <Dialog
    :visible="localVisible"
    :modal="true"
    @hide="onClose"
    class="w-full max-w-[588px]"
    :draggable="false"
    :closable="true"
    :closeOnEscape="true"
    :dismissableMask="false"
    :blockScroll="true"
  >
    <!-- Dialog Header -->
    <template #header>
      <div class="flex justify-between items-center w-full pt-[1px] m-0">
        <h3 class="text-2xl font-bold mb-0">{{ t('dialog.send_message_to') }} {{ accountName }}</h3>
        <button @click="onClose" class="focus:outline-none close-btn -mt-[2px] -mr-[4px]">
          <img src="@/assets/images/elements/close.svg" alt="close" class="w-[24px]"/>
        </button>
      </div>
    </template>

    <!-- Dialog Content -->
    <div class="flex flex-col dialog-content">
      <div>
        <p class="mb-[7px]">{{ t('dialog.your_message') }}</p>
        <Textarea v-model.value="message" class="w-full h-[104px] w-full mb-4 app-input"/>
        <small v-if="errors.message" class="p-error">{{ errors.message }}</small>
      </div>

      <div class="mt-8">
        <Checkbox inputId="anonymous" v-model="stay_anonymous" class="mr-4" :value="1"
                  :binary="true"/>
        <label for="anonymous" class="cursor-pointer text-base">{{ t('dialog.stay_anonymous') }}</label>
      </div>

    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <div class="flex justify-between items-center w-full">
        <Button :label="t('dialog.cancel')"
                class="p-button-primary p-button-sm md:p-button-lg mr-4 w-full border app-input-btn app-input-btn-cancel"
                @click.prevent="onCancel"/>
        <Button :label="t('dialog.send')"
                class="p-button-primary p-button-sm md:p-button-lg ml-4 w-full bg-yellow-400 border-yellow-400 app-input-btn text-lg"
                @click.prevent="onSend"/>
      </div>
    </template>

  </Dialog>
</template>

<script lang="ts" setup>
import {ref, reactive, watch, onMounted, onUnmounted} from 'vue';
import { useFormRequest } from '@/core/composables/forms/formRequest';

const { t } = useI18n();
const toast = useToast();

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  accountName: {
    type: String,
    required: false
  },
  userKey: {
    type: String,
    required: true
  }
});

const emits = defineEmits(['update:isVisible', 'sendMessage']);

const localVisible = ref(props.isVisible);
const message = ref('');
const stay_anonymous = ref(false);
const isSubmitting = ref(false);

watch(() => props.isVisible, (newValue) => {
  localVisible.value = newValue;
});

const formState = reactive({
  message: '',
  stay_anonymous: false,
  userKey: props.userKey,
});

const { submitRequest } = useFormRequest();

const { onSubmit, status, errors: errors } = submitRequest({
  method: 'POST',
  endpoint: 'contact/form',
  form: formState,
  onSuccess: async () => {
    isSubmitting.value = false;
    emits('sendMessage');  // Emit an event when the message is successfully sent
    onClose();  // Close the modal after successful submission
  },
  onError: (errorResponse) => {
    isSubmitting.value = false;
  }
});

// Function to send the message
const onSend = () => {
  isSubmitting.value = true;
  formState.message = message.value;
  formState.stay_anonymous = stay_anonymous.value;

  // Submit the form
  onSubmit();
};

const resetFormState = () => {
  formState.message = '';
  formState.stay_anonymous = false;
  message.value = '';
  stay_anonymous.value = false;
};

const onClose = () => {
  localVisible.value = false;
  resetFormState();
  emits('update:isVisible', false);
};

const onCancel = () => {
  resetFormState();
  onClose();
};

watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    localVisible.value = true;
  } else {
    // Reset form state whenever dialog is closed
    resetFormState();
    localVisible.value = false;
  }
});

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isVisible) {
    emits('update:isVisible', false);
  }
};

</script>

<style scoped>
.close-btn {
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  padding: 2px 0 0 0;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.close-btn:hover {
  background-color: #F8F8F8;
}

.dialog-content {
  padding: 0 15px 25px 0;
}
</style>
